import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Globe2, ArrowLeft, ExternalLink, MapPin,
  Languages, Home as HomeIcon, DollarSign, TrendingUp,
  AlertTriangle, GraduationCap, Calendar, Sparkles,
} from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { Button } from "@/components/ui/button";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


// Language-gate chip colouring: warnings that ADD an admission
// barrier (Swedish-required, Norwegian-required, Icelandic-required,
// French-required, German-required) glow amber. English-taught is
// emerald (no barrier). Anything else falls back to stone.
const LANG_TONE = (lang) => {
  if (!lang) return "stone";
  if (lang.includes("required")) return "amber";
  if (lang.includes("English")) return "emerald";
  return "stone";
};


// Warnings the pack card SHOULD render inline (the honest gotchas).
// Everything else stays in the source page. This keeps cards
// scannable and matches WUA's editorial tone.
const _RENDER_WARNING_SEVERITIES = new Set(["warning"]);


function usdFormat(n) {
  if (n === 0) return "Free";
  if (n == null) return "—";
  return `$${n.toLocaleString()}`;
}


function FlagshipRecord({ rec }) {
  const langTone = LANG_TONE(rec.language);
  const visibleWarnings = (rec.warnings || []).filter(
    (w) => _RENDER_WARNING_SEVERITIES.has(w.severity)
  );
  return (
    <li
      data-testid={`flagship-record-${rec.id}`}
      className="rounded-lg border border-stone-300 bg-white p-6"
    >
      <div className="grid md:grid-cols-12 gap-6">
        {/* Header column */}
        <div className="md:col-span-5">
          <div className="text-xs font-underground-sc tracking-wider text-stone-500 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {rec.city}
          </div>
          <div className="mt-1 font-editorial text-xl leading-tight">
            {rec.university_name || rec.university_id}
          </div>
          <div className="mt-1 text-sm text-stone-700">
            {rec.official_title}
          </div>
          {rec.university_world_rank && (
            <div className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-500">
              World rank: #{rec.university_world_rank}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-1.5 text-[10px] font-underground-sc tracking-wider">
            {rec.language && (
              <span
                data-testid={`flagship-record-lang-${rec.id}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-${langTone}-300 bg-${langTone}-50 text-${langTone}-900`}
              >
                <Languages className="w-3 h-3" />
                {rec.language}
              </span>
            )}
            {typeof rec.psw_eligible_years === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-stone-300 bg-white text-stone-700">
                <HomeIcon className="w-3 h-3" />
                {rec.psw_eligible_years >= 1
                  ? `${rec.psw_eligible_years} yr PSW`
                  : `${Math.round(rec.psw_eligible_years * 12)}-mo PSW`}
              </span>
            )}
            {rec.duration_years && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-stone-300 bg-white text-stone-700">
                <Calendar className="w-3 h-3" />
                {rec.duration_years}-yr {rec.qualification || "BSc"}
              </span>
            )}
          </div>
        </div>

        {/* Numbers column */}
        <div className="md:col-span-3 space-y-3">
          <div>
            <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">
              Non-EU tuition / yr
            </div>
            <div className="font-mono text-lg">
              {usdFormat(rec.tuition_intl_annual_usd)}
            </div>
          </div>
          {typeof rec.offer_rate === "number" && (
            <div>
              <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                Offer rate
              </div>
              <div className="font-mono text-lg">
                {Math.round(rec.offer_rate * 100)}%
              </div>
            </div>
          )}
          {rec.avg_starting_salary_usd && (
            <div>
              <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                Median start salary
              </div>
              <div className="font-mono text-lg">
                {usdFormat(rec.avg_starting_salary_usd)}
              </div>
            </div>
          )}
        </div>

        {/* Honest gotchas column */}
        <div className="md:col-span-4">
          {visibleWarnings.length > 0 && (
            <>
              <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Honest gotchas
              </div>
              <ul className="mt-2 space-y-2 text-xs text-stone-700 leading-relaxed">
                {visibleWarnings.slice(0, 3).map((w, i) => (
                  <li key={i} className="border-l-2 border-amber-400 pl-2">
                    {w.text}
                  </li>
                ))}
              </ul>
            </>
          )}
          {rec.top_employers?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                Top employers
              </div>
              <div className="mt-1 text-xs text-stone-700 leading-relaxed">
                {rec.top_employers.slice(0, 6).join(" · ")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer: evidence + actions */}
      <div className="mt-5 pt-4 border-t border-stone-200 flex flex-wrap items-center justify-between gap-3">
        {rec.evidence && (
          <div className="text-[11px] text-stone-600 font-mono inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
            Verified {rec.evidence.date_checked} against
            <a
              href={rec.evidence.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`flagship-record-source-${rec.id}`}
              className="text-emerald-700 hover:text-emerald-800 truncate max-w-xs inline-flex items-center gap-0.5"
            >
              {new URL(rec.evidence.url).host}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
        <div className="flex items-center gap-3">
          {rec.university_id && (
            <Link
              to={`/uni/${rec.university_id}`}
              data-testid={`flagship-record-uni-link-${rec.id}`}
              className="text-xs font-underground-sc tracking-wider text-stone-700 hover:text-emerald-800 inline-flex items-center gap-1"
            >
              View university
              <ArrowLeft className="w-3 h-3 rotate-180" />
            </Link>
          )}
          {rec.source_url && (
            <a
              href={rec.source_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`flagship-record-programme-link-${rec.id}`}
              className="text-xs font-underground-sc tracking-wider text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
            >
              Open programme page
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </li>
  );
}


export default function FlagshipPackDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchPack() {
      try {
        const r = await fetch(`${BACKEND_URL}/api/flagships/${slug}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setPack(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    }
    setPack(null);
    setError(null);
    fetchPack();
    return () => { cancelled = true; };
  }, [slug]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <SEOHead
          title="Flagship pack not found — WhichUniversityAdvisor"
          description="The requested flagship pack couldn't be found."
        />
        <AlertTriangle className="w-10 h-10 mx-auto text-amber-600 mb-4" />
        <h1 className="font-editorial text-3xl">
          Pack not found
        </h1>
        <p className="mt-4 text-stone-700">
          The flagship pack <code className="font-mono text-sm bg-stone-100 rounded px-1.5 py-0.5">{slug}</code>{" "}
          doesn't exist. Every hand-checked pack lives on the index.
        </p>
        <Button
          onClick={() => navigate("/flagships")}
          data-testid="pack-detail-not-found-back"
          className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Flagships
        </Button>
      </div>
    );
  }

  if (!pack) {
    return (
      <div
        data-testid="pack-detail-loading"
        className="mx-auto max-w-6xl px-6 py-24 animate-pulse"
      >
        <div className="h-4 w-20 bg-stone-200 rounded" />
        <div className="h-14 w-3/4 bg-stone-200 rounded mt-6" />
        <div className="h-4 w-2/3 bg-stone-200 rounded mt-4" />
        <div className="h-4 w-full bg-stone-200 rounded mt-8" />
        <div className="h-4 w-11/12 bg-stone-200 rounded mt-2" />
        <div className="mt-10 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 rounded-lg bg-stone-100" />
          ))}
        </div>
      </div>
    );
  }

  const seoTitle = `${pack.name} — ${pack.count} hand-checked bachelors · ${pack.country}`;

  return (
    <div
      data-testid={`flagship-pack-detail-${pack.slug}`}
      className="mx-auto max-w-6xl px-6 py-14 lg:py-20"
    >
      <SEOHead
        title={seoTitle}
        description={
          `${pack.count} hand-checked bachelor programmes across ` +
          `${pack.country}. ${pack.editor_note.slice(0, 140)}...`
        }
      />

      {/* Breadcrumb */}
      <Link
        to="/flagships"
        data-testid="pack-detail-back"
        className="inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-stone-600 hover:text-emerald-800"
      >
        <ArrowLeft className="w-3 h-3" />
        All Flagship Packs
      </Link>

      {/* Header */}
      <section
        data-testid="pack-detail-hero"
        className="mt-6 grid md:grid-cols-3 gap-10 items-start"
      >
        <div className="md:col-span-2">
          <div className="label-uppercase text-emerald-700 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Hand-checked flagship pack
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight tracking-tight">
            <span className="text-4xl mr-3">{pack.flag}</span>
            {pack.name}
          </h1>
          <p className="mt-4 text-stone-700 leading-relaxed text-lg">
            {pack.tagline}
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-xs">
            <span
              data-testid="pack-detail-count-chip"
              className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-full px-3 py-1.5"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-underground-sc tracking-wider">
                {pack.count} records · every one uni-verified
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Globe2 className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">
                {pack.country}
              </span>
            </span>
          </div>
        </div>

        {/* Editor's note */}
        <aside
          data-testid="pack-detail-editor-note"
          className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6"
        >
          <div className="label-uppercase !text-[10px] text-stone-500 inline-flex items-center gap-1">
            <GraduationCap className="w-3 h-3" /> The editor's note
          </div>
          <p className="mt-3 text-sm text-stone-800 leading-relaxed">
            {pack.editor_note}
          </p>
        </aside>
      </section>

      {/* Records */}
      <section
        data-testid="pack-detail-records"
        className="mt-14"
      >
        <div className="label-uppercase text-stone-500">
          The {pack.count} records
        </div>
        <h2 className="font-editorial text-2xl mt-2 leading-tight">
          Ranked by world rank · verified 2026-02-27
        </h2>
        <ol className="mt-6 space-y-5">
          {pack.records.map((r) => (
            <FlagshipRecord key={r.id} rec={r} />
          ))}
        </ol>
      </section>

      {/* Cross-links */}
      <section
        data-testid="pack-detail-cross-links"
        className="mt-16 rounded-lg border border-stone-300 bg-white p-8"
      >
        <div className="label-uppercase text-stone-500">Related</div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/flagships">
            <Button
              variant="outline"
              size="sm"
              data-testid="pack-detail-cross-flagships"
              className="rounded-md"
            >
              Every Flagship Pack (13)
            </Button>
          </Link>
          {(pack.slug === "nordic-trio" || pack.slug === "is-reykjavik") && (
            <>
              <Link to="/nordic-tuition-reality">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="pack-detail-cross-tuition-reality"
                  className="rounded-md"
                >
                  Nordic Tuition Reality
                </Button>
              </Link>
              <Link to="/nordic-flagship-corridor">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="pack-detail-cross-corridor"
                  className="rounded-md"
                >
                  Walk the 12-record Nordic Corridor
                </Button>
              </Link>
            </>
          )}
          <Link
            to={`/advanced?preset=flagships&pack=${pack.slug}`}
          >
            <Button
              size="sm"
              data-testid="pack-detail-run-match"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
              Run these against my profile
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
